import {
  useQuery,
  UseQueryOptions,
  useMutation,
  UseMutationOptions,
  useQueryClient,
} from '@tanstack/react-query'
import {
  fetchAllCompanies,
  fetchCompanyById,
  fetchCompanies,
  updateCompanyContacts,
} from '../methods'
import {
  Company,
  PaginatedCompany,
  CompanyContact,
  CompanyQueriesFilter,
} from 'types/company'

export function useCompany() {
  const queryClient = useQueryClient()

  // Hook for fetching a single company by ID
  const queryCompany = (
    companyId: string,
    options?: {
      queryOptions?: UseQueryOptions<Company, Error>
      enabled?: boolean
    }
  ) => {
    return useQuery<Company>({
      queryKey: ['company', companyId],
      queryFn: () => fetchCompanyById(companyId),
      staleTime: 5 * 60 * 1000, // 5 minutes default
      refetchOnWindowFocus: false,
      enabled: options?.enabled !== false && !!companyId,
      gcTime: 30 * 60 * 1000, // Cache company for 30 minutes since it doesn't change often
      ...options?.queryOptions,
    })
  }

  // Hook for fetching all companies
  const queryCompanies = (
    filters?: CompanyQueriesFilter,
    options?: {
      queryOptions?: UseQueryOptions<any>
      enabled?: boolean
    }
  ) => {
    return useQuery<PaginatedCompany>({
      queryKey: ['companies', filters],
      queryFn: () => fetchCompanies(filters),
      staleTime: 5 * 60 * 1000, // 5 minutes default
      refetchOnWindowFocus: false,
      enabled: options?.enabled !== false, // Enabled by default unless explicitly disabled
      gcTime: 30 * 60 * 1000, // Cache companies for 30 minutes since they don't change often
      ...options?.queryOptions,
    })
  }

  /** Loads all pages from `/customers` (see {@link fetchAllCompanies}). */
  const queryAllCompanies = (
    filters?: Omit<CompanyQueriesFilter, 'page' | 'limit'>,
    options?: {
      queryOptions?: UseQueryOptions<any>
      enabled?: boolean
    }
  ) => {
    return useQuery<PaginatedCompany>({
      queryKey: ['companies', 'all-pages', filters],
      queryFn: () => fetchAllCompanies(filters),
      staleTime: 5 * 60 * 1000,
      refetchOnWindowFocus: false,
      enabled: options?.enabled !== false,
      gcTime: 30 * 60 * 1000,
      ...options?.queryOptions,
    })
  }

  // Hook for updating company contacts
  const updateCompanyContactsMutation = (options?: {
    mutationOptions?: UseMutationOptions<
      Company,
      Error,
      {
        companyId: string
        contactData: CompanyContact[]
      }
    >
  }) => {
    return useMutation<
      Company,
      Error,
      {
        companyId: string
        contactData: CompanyContact[]
      }
    >({
      mutationFn: ({ companyId, contactData }) =>
        updateCompanyContacts(companyId, contactData),
      onSuccess: (data, variables) => {
        // Invalidate and refetch company queries to update the cache
        // This will refetch both the specific company and the companies list
        queryClient.invalidateQueries({
          queryKey: ['company', variables.companyId],
        })
        queryClient.invalidateQueries({ queryKey: ['companies'] })
      },
      ...options?.mutationOptions,
    })
  }

  return {
    queryCompany,
    queryCompanies,
    queryAllCompanies,
    updateCompanyContactsMutation,
  }
}
